import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Heart, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { collectionAPI, membershipAPI, wishlistAPI } from '../lib/api';
import CollectionSelectorModal from './CollectionSelectorModal';
import PriceInputModal from './PriceInputModal';
import PriceDisplay from './PriceDisplay';

// WO-35: "Add to Collection" / "Add to Wishlist" for every product id: other games' cards (GameCardPage) and sealed
// products (ProductPage). Same flow and endpoints as the Pokemon card page (pages/Card.js): collection picker ->
// price/condition -> POST /api/collections/collections/<id>/items; wishlist with an optional target price. The
// endpoints take any products.product_id (their foreign key is on products), so no API change is needed to store them.
export default function CollectButtons({ itemId, itemName, currentPrice = null, returnPath, sealed = false }) {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const [inCollection, setInCollection] = useState(false);
  const [collectionIds, setCollectionIds] = useState([]);
  const [inWishlist, setInWishlist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [collectionId, setCollectionId] = useState(null);
  const [showWishPrompt, setShowWishPrompt] = useState(false);
  const [target, setTarget] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !itemId) return undefined;
    let live = true;
    membershipAPI.getStatus([itemId]).then(({ data }) => {
      const row = data?.[itemId];
      if (!live || !row) return;
      setInCollection(!!row.in_collection);
      setCollectionIds(row.collection_ids || []);
      setInWishlist(!!row.in_wishlist);
    });
    return () => { live = false; };
  }, [user, itemId]);

  const requireLogin = () => {
    sessionStorage.setItem('redirectAfterLogin', returnPath || window.location.pathname);
    navigate('/login');
  };

  const fail = (msg) => { setError(msg); setTimeout(() => setError(null), 5000); };

  const onCollection = async () => {
    if (!user) { requireLogin(); return; }
    if (!inCollection) { setShowSelector(true); return; }
    setBusy(true);
    try {
      for (const cid of collectionIds) {
        // eslint-disable-next-line no-await-in-loop
        await collectionAPI.removeFromCollection(cid, itemId);
      }
      setInCollection(false);
      setCollectionIds([]);
    } catch (e) {
      fail('Could not remove it from your collection.');
    } finally {
      setBusy(false);
    }
  };

  const onPriceConfirm = async ({ price, currency: inputCurrency, condition, quantity }) => {
    setShowPriceInput(false);
    setBusy(true);
    try {
      const res = await collectionAPI.addToCollection(collectionId, itemId, {
        quantity,
        condition: sealed ? 'Sealed' : condition,
        purchase_price: price,
        currency: inputCurrency || currency,
      });
      if (res?.error || res?.detail) throw new Error('add failed');
      setInCollection(true);
      setCollectionIds((ids) => [...new Set([...ids, collectionId])]);
    } catch (e) {
      fail('Could not add it to your collection.');
    } finally {
      setBusy(false);
      setCollectionId(null);
    }
  };

  const onWishlist = async () => {
    if (!user) { requireLogin(); return; }
    if (!inWishlist) { setTarget(''); setShowWishPrompt(true); return; }
    setBusy(true);
    try {
      await wishlistAPI.removeFromWishlist(itemId);
      setInWishlist(false);
    } catch (e) {
      fail('Could not remove it from your wishlist.');
    } finally {
      setBusy(false);
    }
  };

  const onWishConfirm = async () => {
    setShowWishPrompt(false);
    setBusy(true);
    try {
      const value = target ? parseFloat(target) : null;
      const res = await wishlistAPI.addToWishlist(itemId, { target_price: Number.isFinite(value) ? value : null, currency });
      if (res?.error || res?.detail) throw new Error('add failed');
      setInWishlist(true);
    } catch (e) {
      fail('Could not add it to your wishlist.');
    } finally {
      setBusy(false);
    }
  };

  const spinner = <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />;
  return (
    <div className="flex flex-col items-start md:items-end gap-1" data-testid="collect-buttons">
      <div className="flex gap-2">
        <button type="button" onClick={onCollection} disabled={busy} data-testid="add-to-collection"
                title={inCollection ? 'Remove from Collection' : 'Add to Collection'}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 ${
                  inCollection ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-purple hover:bg-purple/90 text-cream'}`}>
          {busy ? spinner : inCollection ? <Check size={16} /> : <Plus size={16} />}
          <span>{inCollection ? 'In Collection' : 'Add to Collection'}</span>
        </button>
        <button type="button" onClick={onWishlist} disabled={busy} data-testid="add-to-wishlist"
                title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 ${
                  inWishlist ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-dark border border-purple/30 hover:bg-purple/20 text-cream'}`}>
          {busy ? spinner : <Heart size={16} className={inWishlist ? 'fill-current' : ''} />}
          <span>{inWishlist ? 'Wishlisted' : 'Add to Wishlist'}</span>
        </button>
      </div>
      {error && <div className="text-xs text-red-400" role="alert">{error}</div>}

      <CollectionSelectorModal isOpen={showSelector} onClose={() => setShowSelector(false)}
                               onSelectCollection={(cid) => { setCollectionId(cid); setShowSelector(false); setShowPriceInput(true); }}
                               cardId={itemId} cardName={itemName || 'this item'} />
      <PriceInputModal isOpen={showPriceInput} onClose={() => { setShowPriceInput(false); setCollectionId(null); }}
                       onConfirm={onPriceConfirm} cardId={itemId} cardName={itemName || 'this item'}
                       currentPrice={currentPrice} />
      {showWishPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-purple/20 rounded-lg max-w-sm w-full text-left">
            <div className="p-4 border-b border-purple/20">
              <h2 className="text-lg font-semibold text-cream">Add to Wishlist</h2>
              <p className="text-sm text-gray-400 mt-1">{itemName}</p>
            </div>
            <div className="p-4 space-y-4">
              {currentPrice ? (
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <span className="text-sm text-gray-400">Current price</span>
                  <PriceDisplay amountCad={currentPrice} className="text-xl font-bold text-green-400" />
                </div>
              ) : null}
              <label className="block text-sm font-medium text-gray-300">
                Target Price (optional)
                <input type="number" step="0.01" min="0" placeholder="e.g. 5.00" value={target}
                       onChange={(e) => setTarget(e.target.value)} data-testid="wishlist-target"
                       className="mt-2 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple" />
              </label>
            </div>
            <div className="flex gap-3 p-4 border-t border-purple/20">
              <button type="button" onClick={() => setShowWishPrompt(false)}
                      className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg">Cancel</button>
              <button type="button" onClick={onWishConfirm}
                      className="flex-1 px-4 py-2 bg-purple hover:bg-purple/90 text-white rounded-lg">Add to Wishlist</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
